/**
 * lib/uiState.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Global registry of "what's currently open / selected / focused" in the UI.
 * Snapshotted at error time and attached to every error report so the admin
 * error log can answer "which modal was open when the crash happened" without
 * us having to guess from the stack trace.
 *
 * Module-level state — one registry per browser tab. Components opt in by
 * calling registerUiState/unregisterUiState (or the useUiState hook below).
 *
 * Usage:
 *
 *   // Inside a modal component:
 *   useUiState('modal:bug_report', open ? 'open' : null)
 *
 *   // Inside a slideout component:
 *   useUiState('slideout:product_detail', selectedProduct?.id ?? null)
 *
 *   // The error reporter calls:
 *   const snapshot = getUiStateSnapshot()
 *   //  → { 'modal:bug_report': 'open', 'slideout:product_detail': 'abc-123' }
 *
 * Keys SHOULD be namespaced (`modal:`, `slideout:`, `view:`, `selection:`).
 * Values SHOULD be strings or numbers — keep snapshots small and JSON-safe.
 * Setting value to `null` removes the key.
 */

import { useEffect } from 'react'

type UiStateValue = string | number | boolean | null

const registry = new Map<string, Exclude<UiStateValue, null>>()

/**
 * Register a UI state value. Pass `null` to remove the key.
 * No-op during SSR.
 */
export function registerUiState(key: string, value: UiStateValue): void {
  if (typeof window === 'undefined') return
  if (value === null || value === undefined) {
    registry.delete(key)
  } else {
    registry.set(key, value)
  }
}

/**
 * Remove a key from the registry. Equivalent to `registerUiState(key, null)`.
 */
export function unregisterUiState(key: string): void {
  registry.delete(key)
}

/**
 * Capture a JSON-safe snapshot of all currently-registered UI state.
 * Returns an empty object when nothing is registered (and during SSR).
 */
export function getUiStateSnapshot(): Record<string, Exclude<UiStateValue, null>> {
  const out: Record<string, Exclude<UiStateValue, null>> = {}
  for (const [k, v] of registry.entries()) out[k] = v
  return out
}

/**
 * Hook that mirrors a value into the global UI state registry for as long as
 * the component is mounted. Pass `null` (or any falsy non-zero value) to
 * remove the key without unmounting.
 *
 * The cleanup effect removes the key on unmount so stale entries don't pile
 * up across navigation.
 */
export function useUiState(key: string, value: UiStateValue): void {
  useEffect(() => {
    registerUiState(key, value)
    return () => unregisterUiState(key)
    // We intentionally re-run on every value change so toggling open→closed
    // updates the registry in real time.
  }, [key, value])
}
